import { createContext, ReactNode, useContext } from 'react';
import { useAuth as useClerkAuth, useUser as useClerkUser } from '@clerk/clerk-react';
import { config } from '../../utils/config';
import { ClerkProvider } from '@providers/ClerkProvider/ClerkProvider';

export interface AuthContextValue {
  isLoaded: boolean;
  isSignedIn: boolean;
  getToken: () => Promise<string | null>;
  signOut: () => void | Promise<void>;
  email?: string;
}

const MOCK_AUTH: AuthContextValue = {
  isLoaded: true,
  isSignedIn: true,
  getToken: () => Promise.resolve(null),
  signOut: () => Promise.resolve(),
  email: 'localhost@strideup.co',
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);

  if (value === null) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return value;
}

function ClerkAuthBridge({ children }: { children: ReactNode }) {
  const clerkAuth = useClerkAuth();
  const { user } = useClerkUser();

  const value: AuthContextValue = {
    isLoaded: Boolean(clerkAuth.isLoaded),
    isSignedIn: Boolean(clerkAuth.isSignedIn),
    getToken: clerkAuth.getToken,
    signOut: clerkAuth.signOut,
    email: user?.primaryEmailAddress?.emailAddress,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  if (config.isLocalAuth) {
    return <AuthContext.Provider value={MOCK_AUTH}>{children}</AuthContext.Provider>;
  }

  return (
    <ClerkProvider>
      <ClerkAuthBridge>{children}</ClerkAuthBridge>
    </ClerkProvider>
  );
}
